import React from 'react';
import { MapPin, Bed, Bath, Ruler, Image } from 'lucide-react';

const AccommodationCard = ({ accommodation, onClick }) => {
  const images = accommodation.images && accommodation.images.length > 0
    ? accommodation.images
    : ['https://via.placeholder.com/400x300?text=No+Image'];

  const title = accommodation.title || accommodation.name;
  const price = Number(accommodation.price) || 0;
  const priceType = accommodation.price_type || 'Fixed Price';

  return (
    <div
      onClick={onClick}
      className="group flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300 cursor-pointer overflow-hidden w-[calc(100%-1rem)] mx-auto sm:w-full"
    >
      {/* Image */}
      <div className="relative h-52 overflow-hidden bg-gray-100">
        <img
          src={images[0]}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Availability badge */}
        {accommodation.is_available !== false && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-green-500 text-white text-[11px] font-semibold shadow-md">
            Available
          </span>
        )}

        {/* Property type badge */}
        <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-blue-600 text-[11px] font-bold shadow-md">
          {accommodation.property_type || accommodation.type}
        </span>

        {/* Image count */}
        {images.length > 1 && (
          <span className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/60 text-white text-xs font-medium backdrop-blur-sm">
            <Image className="w-3.5 h-3.5" />
            {images.length}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <p className="flex items-center gap-1 text-xs text-gray-500 mb-1.5 truncate">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{accommodation.location}</span>
        </p>

        <h3 className="text-[15px] font-bold text-gray-900 leading-snug mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {title}
        </h3>

        {/* Features */}
        <div className="grid grid-cols-3 gap-2 text-[13px] text-gray-600 mb-4">
          <div className="flex items-center gap-1.5">
            <Bed className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span>{accommodation.bedrooms} bed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Bath className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span>{accommodation.bathrooms} bath</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Ruler className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span>{accommodation.square_feet ? `${accommodation.square_feet} sq ft` : '—'}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 mt-auto border-t border-gray-100">
          <div className="leading-tight">
            <p className="text-lg font-extrabold text-blue-600">
              GH₵{price.toLocaleString()}
            </p>
            <p className="text-[11px] text-gray-400">per month</p>
          </div>
          <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-semibold">
            {priceType}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AccommodationCard;
